/**
 * What the balance pill SHOWS, as distinct from what the balance IS.
 *
 * The server's number is the truth and arrives whenever it arrives: with a deal's answer, with a session
 * read, with a notification. A coin flight is decoration on top of it — the stake leaving the pill, a
 * win landing in it — and the number should move with the coins, not jump ahead of them. So the pill
 * reads from here, and here decides what it shows:
 *
 *   - with no flight in progress, the truth, straight away (no animation for a number that just changed
 *     for its own reasons: a daily top-up, another device);
 *   - while a flight holds the display, the flight's own number, counting from where the player last
 *     SAW it toward where it must end — and the truth is only remembered, never shown, until the
 *     flight lets go;
 *   - when the last flight lets go, the truth. Always exactly the truth: the flight decides how the
 *     number gets there, never where it ends.
 *
 * That is what makes a refresh arriving before, during or after a flight safe. Before or during: it
 * updates the truth the flight is heading for and changes nothing on screen. After: the flight has
 * already ended on the value the refresh reports, so nothing moves — and if the two ever differ, the
 * truth wins and the number lands on it.
 *
 * DOM-free on purpose: the sink is whatever paints the pill(s).
 */

export interface Hold {
  /** The display is at exactly `n` (a step of the count). Ignored once released. */
  show(n: number): void;
  /**
   * Let go: the display returns to the truth (once every hold has). Idempotent.
   *
   * `expected` is what the flight was heading for. If the server has NOT yet reported anything new (its
   * balance is still what the player saw when the hold began, or unknown), the display stays on
   * `expected` rather than falling back to the old number — the refresh is late, not wrong — and the
   * truth replaces it the moment it arrives, whatever it is.
   */
  release(expected?: number): void;
  /** The server's latest balance, or null if it has not been heard yet. */
  readonly truth: number | null;
  /** The number the player was looking at when this hold began. */
  readonly from: number | null;
}

export class BalanceDisplay {
  readonly #paint: (n: number) => void;
  #truth: number | null = null;
  #shown: number | null = null;
  #holds = new Set<symbol>();
  #marked: number | null = null;

  constructor(paint: (n: number) => void) {
    this.#paint = paint;
  }

  /** The server's balance. Shown at once unless a flight is holding the display. */
  setTruth(n: number): void {
    if (!Number.isFinite(n)) return;
    this.#truth = n;
    if (this.#holds.size === 0) this.#set(n);
  }

  get truth(): number | null {
    return this.#truth;
  }

  /** What the pill shows now. */
  get shown(): number | null {
    return this.#shown;
  }

  get holding(): boolean {
    return this.#holds.size > 0;
  }

  /**
   * Remember what the player is looking at right now — the round has just ended and a payout may be
   * on its way. A win flight counts up FROM this, whichever order the refresh and the flight arrive
   * in, because by the time the flight starts the pill may already have been told the new number.
   */
  mark(): void {
    this.#marked = this.#shown;
  }

  /** The value set by the last `mark()`, once, or the value shown now. */
  takeMark(): number | null {
    const m = this.#marked ?? this.#shown;
    this.#marked = null;
    return m;
  }

  /**
   * Hold the display at `from` (default: what it shows now) for a flight. The returned handle steps the
   * number and lets go.
   */
  hold(from: number | null = this.#shown): Hold {
    const id = Symbol('hold');
    this.#holds.add(id);
    if (from !== null) this.#set(from);
    const self = this;
    return {
      show(n) {
        if (self.#holds.has(id) && Number.isFinite(n)) self.#set(Math.round(n));
      },
      release(expected) {
        if (!self.#holds.delete(id)) return;
        if (self.#holds.size > 0) return;
        // The last one out puts the truth back — exactly, whatever the flight thought it was heading for —
        // unless the truth is only what the player already saw: then the flight's own destination stays
        // until the real number arrives.
        const stale = self.#truth === null || self.#truth === from;
        if (expected !== undefined && stale && Number.isFinite(expected)) self.#set(Math.round(expected));
        else if (self.#truth !== null) self.#set(self.#truth);
      },
      get truth() {
        return self.#truth;
      },
      from,
    };
  }

  #set(n: number): void {
    this.#shown = n;
    this.#paint(n);
  }
}

/**
 * The numbers a flight of `coins` steps through, from `from` to `to`, ending EXACTLY on `to`. Integer
 * steps, non-decreasing when counting up (non-increasing when counting down), evenly spread.
 */
export function countSteps(from: number, to: number, coins: number): number[] {
  const n = Math.max(1, Math.floor(coins));
  const out: number[] = [];
  for (let i = 1; i <= n; i += 1) out.push(i === n ? to : Math.round(from + ((to - from) * i) / n));
  return out;
}
